import { GameState } from '../core/GameState';
import { bn, Decimal, ZERO } from '../core/BigNumber';
import { BUILDINGS } from '../data/buildings';
import { eventBus } from '../core/EventBus';
import { UpgradeSystem } from './UpgradeSystem';
import { AchievementSystem } from './AchievementSystem';

export class CpsCalculator {
  constructor(
    private upgradeSystem: UpgradeSystem,
    private achievementSystem: AchievementSystem,
  ) {}

  recalculate(state: GameState): void {
    let totalCps = ZERO;
    let globalMult = bn(1);

    globalMult = globalMult.mul(this.upgradeSystem.getCookieMultiplier(state));
    globalMult = this.applyMilkMult(state, globalMult);
    globalMult = this.applyPrestigeMult(state, globalMult);
    globalMult = this.applyBuffMults(state, globalMult);

    state.globalCpsMult = globalMult;

    for (const def of BUILDINGS) {
      const bs = state.buildings.get(def.id);
      if (!bs || bs.amount === 0) continue;

      let buildingCps = bn(def.baseCps);
      buildingCps = buildingCps.mul(this.upgradeSystem.getBuildingMultiplier(state, def.id));
      buildingCps = buildingCps.mul(1 + bs.level * 0.01);

      const thisBuildingTotal = buildingCps.mul(bs.amount).mul(globalMult);
      totalCps = totalCps.add(thisBuildingTotal);
    }

    state.cookiesPs = totalCps;
    state.cookiesPsRaw = totalCps;
    if (totalCps.gt(state.cookiesPsRawHighest)) {
      state.cookiesPsRawHighest = totalCps;
    }

    eventBus.emit('gains:updated', totalCps);
  }

  /** CpS contribution from a single building type. */
  getBuildingCps(state: GameState, buildingId: string): Decimal {
    const def = BUILDINGS.find((b) => b.id === buildingId);
    if (!def) return ZERO;

    const bs = state.buildings.get(buildingId);
    if (!bs || bs.amount === 0) return ZERO;

    let buildingCps = bn(def.baseCps);
    buildingCps = buildingCps.mul(this.upgradeSystem.getBuildingMultiplier(state, buildingId));
    buildingCps = buildingCps.mul(1 + bs.level * 0.01);

    return buildingCps.mul(bs.amount).mul(state.globalCpsMult);
  }

  getClickCookies(state: GameState): Decimal {
    let base = bn(1);

    const nonCursors = this.getTotalNonCursors(state);
    if (state.upgrades.get('cursor_t4')?.bought) {
      base = base.add(bn(0.1).mul(nonCursors));
    }
    if (state.upgrades.get('cursor_t5')?.bought) {
      base = base.add(bn(0.5).mul(nonCursors));
    }
    if (state.upgrades.get('cursor_t6')?.bought) {
      base = base.add(bn(5).mul(nonCursors));
    }
    if (state.upgrades.get('cursor_t7')?.bought) {
      base = base.add(bn(50).mul(nonCursors));
    }

    let clickMult = bn(1);
    if (state.upgrades.get('plastic_mouse')?.bought) {
      clickMult = clickMult.add(state.cookiesPs.mul(0.01));
    }
    if (state.upgrades.get('iron_mouse')?.bought) {
      clickMult = clickMult.add(state.cookiesPs.mul(0.01));
    }
    if (state.upgrades.get('titanium_mouse')?.bought) {
      clickMult = clickMult.add(state.cookiesPs.mul(0.01));
    }

    state.buffs.forEach((buff) => {
      if (buff.multClick > 0) {
        clickMult = clickMult.mul(buff.multClick);
      }
    });

    return base.mul(clickMult).mul(state.globalCpsMult);
  }

  private applyMilkMult(state: GameState, mult: Decimal): Decimal {
    const milk = this.achievementSystem.getMilkProgress(state);
    if (milk > 0) {
      return mult.mul(1 + milk * 0.05);
    }
    return mult;
  }

  private applyPrestigeMult(state: GameState, mult: Decimal): Decimal {
    if (state.prestige.level.gt(0) && state.prestige.heavenlyUpgrades.has('heavenly_cookies')) {
      return mult.mul(state.prestige.level.mul(0.01).add(1));
    }
    return mult;
  }

  private applyBuffMults(state: GameState, mult: Decimal): Decimal {
    state.buffs.forEach((buff) => {
      if (buff.multCpS > 0) {
        mult = mult.mul(buff.multCpS);
      }
    });
    return mult;
  }

  private getTotalNonCursors(state: GameState): number {
    let total = 0;
    state.buildings.forEach((bs, id) => {
      if (id !== 'cursor') total += bs.amount;
    });
    return total;
  }
}
